import { lazy } from "react";

// Every page is code-split. After a new deploy the previous build's hashed chunk
// files are gone, so a visitor still on the old version (an open tab, or HTML the
// browser cached) fails to load the next page they open. Reloading once fetches
// the new index.html and chunk names. The timestamp guard stops a reload loop
// when the chunk is genuinely unavailable (offline, server down); the error then
// reaches ErrorBoundary instead.
const RELOAD_KEY = "dc-chunk-reload-at";
const RELOAD_GUARD_MS = 10000;

const isChunkLoadError = (error) =>
  error?.name === "ChunkLoadError" ||
  /Loading (CSS )?chunk [\w-]+ failed/i.test(error?.message || "");

const claimReload = () => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
    if (Date.now() - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    return true;
  } catch (e) {
    // No storage means no loop guard, so don't risk reloading forever.
    return false;
  }
};

const lazyWithRetry = (factory) =>
  lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (isChunkLoadError(error) && claimReload()) {
        window.location.reload();
        // Keep the Suspense fallback on screen while the page reloads.
        return new Promise(() => {});
      }
      throw error;
    }
  });

export default lazyWithRetry;
